import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import * as api from '../../services/api';
import type { CriterionTranscriptionAssignment, User } from '../../types';
import SearchableSelect from '../common/SearchableSelect';
import { type AssignmentPair, pairCopyBulkRequests } from './assignToOthers';

interface Props {
  open: boolean;
  pairs: AssignmentPair[];
  users: User[];
  onClose: () => void;
  onAssigned: (result: {
    created: CriterionTranscriptionAssignment[];
    createdCount: number;
    userCount: number;
  }) => void;
}

const pairLabel = (count: number): string =>
  `${count} criterion-transcript pair${count === 1 ? '' : 's'}`;

const personLabel = (count: number): string => `${count} ${count === 1 ? 'person' : 'people'}`;

const AssignToOthersDialog = ({ open, pairs, users, onClose, onAssigned }: Props) => {
  const [userIds, setUserIds] = useState<string[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
      ),
    [users]
  );

  const close = () => {
    if (assigning) return;
    setUserIds([]);
    setError(null);
    onClose();
  };

  const assign = async () => {
    if (userIds.length === 0 || pairs.length === 0) return;
    setAssigning(true);
    setError(null);
    try {
      const results = await Promise.all(
        pairCopyBulkRequests(pairs, userIds).map(request =>
          api.bulkCreateAssignments(request.criteriaId, request.userIds, request.transcriptionIds)
        )
      );
      const created = results.flatMap(result => result.assignments);
      const createdCount = results.reduce((sum, result) => sum + result.createdCount, 0);
      onAssigned({ created, createdCount, userCount: userIds.length });
    } catch {
      setError('The selected pairs could not be assigned.');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Assign to others</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Copy {pairLabel(pairs.length)} to the people you choose. Already-assigned pairs are
          skipped.
        </Typography>
        <FormControl size="small" fullWidth>
          <InputLabel id="assign-to-others-label">Assign to</InputLabel>
          <SearchableSelect
            labelId="assign-to-others-label"
            multiple
            value={userIds}
            label="Assign to"
            onChange={event =>
              setUserIds(
                typeof event.target.value === 'string'
                  ? event.target.value.split(',')
                  : event.target.value
              )
            }
            options={sortedUsers.map(user => ({
              value: user.id,
              label: `${user.firstName} ${user.lastName} (${user.userType})`,
            }))}
            renderValue={selected => `${(selected as string[]).length} selected`}
          />
        </FormControl>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={assigning}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={userIds.length === 0 || pairs.length === 0 || assigning}
          onClick={assign}
        >
          {assigning ? (
            <CircularProgress size={18} />
          ) : (
            `Assign ${pairLabel(pairs.length)} to ${personLabel(userIds.length)}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignToOthersDialog;
